import React from 'react';
import toastr from 'react-hot-toast';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import { alphabeticalSort } from 'utilities/sort';
import type { DropdownOption } from 'types/common/DropdownOption';
import {
  FilterParameters,
  generateFilterExpression,
  generateIncludeQuery,
  generatePaginationQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { Workspace } from 'types/Workspace';
import type { WorkspaceUserResponse } from 'types/WorkspaceUser';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import { useCurrentUser } from 'hooks/common/useCurrentUser';

const getWorkspaces = (
  currentUserID: string,
  query: string,
  pageParam: number
) => {
  if (!currentUserID) {
    Promise.reject('user ID not found');
    return;
  }

  const filterParams: FilterParameters[] = [
    {
      key: 'userID',
      value: currentUserID,
      type: 'equal',
    },
    {
      key: 'workspace.name',
      value: query,
      type: 'contains',
    },
  ];

  const queryArray = [
    generateIncludeQuery(['workspace']),
    generateFilterExpression('and', filterParams),
    generatePaginationQuery({ pageNumber: pageParam, pageSize: 10 }),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.WORKSPACE_USERS, queryArray);
  return fetchWrapper<WorkspaceUserResponse>({
    url: url,
    method: 'GET',
  });
};

type WorkspaceSearchFilters = {
  query: string;
};

export function useInfiniteWorkspaceSearch(
  workspaceSearchFilters: WorkspaceSearchFilters,
  enabled = true
) {
  const { query } = workspaceSearchFilters;
  const { currentUserID } = useCurrentUser();
  const queryClient = useQueryClient();

  const queryResult = useInfiniteQuery(
    ['workspaces search', query],
    ({ pageParam = 1 }) => getWorkspaces(currentUserID || '', query, pageParam),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage?.data?.length ? allPages?.length + 1 : undefined;
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Workspaces: ${error.message}`);
        }
      },
      enabled: !!currentUserID && enabled,
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  React.useEffect(() => {
    return () => {
      queryClient.cancelQueries(['workspaces search', query]);
    };
  }, [queryClient, query]);

  const { data: workspaceResponse } = queryResult;

  const allWorkspaceUsers = workspaceResponse?.pages.reduce<any[]>(
    (acc, page) => {
      if (!page?.data) {
        return acc;
      }
      return [...acc, ...page.data];
    },
    []
  );

  // We need to collect all included items from all pages
  const allIncluded = workspaceResponse?.pages.reduce<any[]>((acc, page) => {
    if (!page?.included) {
      return acc;
    }
    return [...acc, ...page.included];
  }, []);

  const workspaces = filterIncludedItem<Workspace>(
    allIncluded,
    SPARK_RESOURCE_TYPES.WORKSPACES
  );

  const idMap = React.useMemo(
    () =>
      workspaces?.reduce<Record<string, Workspace>>((acc, curr) => {
        return { ...acc, [curr.id]: curr };
      }, {}) || {},
    [workspaces]
  );

  const sortedWorkspaces = React.useMemo(
    () =>
      workspaces?.sort((a, b) =>
        alphabeticalSort(a.attributes.name, b.attributes.name)
      ) || [],
    [workspaces]
  );

  const dropdownOptions = React.useMemo(
    () =>
      sortedWorkspaces?.map((workspace) => ({
        label: workspace.attributes.name,
        value: workspace.id,
        iconUrl: workspace.attributes.imageURL || '',
      })) || [],
    [sortedWorkspaces]
  );

  return {
    workspaces: sortedWorkspaces,
    workspaceUsers: allWorkspaceUsers,
    idMap,
    dropdownOptions,
    ...queryResult,
  };
}
